// Strip + revealed panel layout (post-lead-capture).
//
// The TOP STRIP shows only Location (zip/city search or selected pill).
// Optional strip fields live in FittingTypeField.tsx and DateFieldStrip.tsx.
// Selecting a location reveals the full panel below — fitting type, date,
// fitter, times, and Book Fitting — with progressive enablement:
//   location → fitting type → date + fitter → times → book CTA.
//
// Design comp reference: screenshots 2026-05-25 6:18:01 through 6:18:42.

import styles from '../styles/widget.module.css';
import swingerLogo from '../assets/images/swinger-logo.png';
import { visibleFittingTypes, pricingLabelFor, fittingDisplayFor } from '../data/fittingTypes';
import { getBookingPanelEnablement } from '../utils/bookingFlow';
import { formatPricingLines } from '../utils/pricing';
import { storeDisplayName } from '../data/storeDisplayNames';
import { promoPricingLines } from '../data/promoPricing';
import { promoPriceShowsFlatFee } from '../config/countries';
import { MobileSelectionSummary } from '../components/MobileSelectionSummary';
import { FitterField } from '../components/FitterField';
import { TimeSlots } from './TimeSlots';
import { PromoConfirm } from './PromoConfirm';
import { Calendar } from './Calendar';
import type { BookingAction, BookingState } from '../state/machine';
import { useEffect, useRef } from 'react';
import { useOpenClose } from '../hooks/useOpenClose';
import SimplePopup from '../components/SimplePopup';
import { FittingTypeField } from '../components/FittingTypeField';
import { DateFieldStrip } from '../components/DateFieldStrip';
import { useAvailableDates } from '../hooks/useAvailableDates';

interface BookingStripPanelProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onBook: () => void;
  /** Called from TimeSlots' no-availability empty state to POST /waitlist. */
  onJoinWaitlist: () => void;
  /** Click on any of the strip's Name/Email/Studio "Change" CTAs sends
   *  the user back to the combined LeadCapture step 1 (rather than
   *  clearing a single field and dropping them on the empty strip). */
  onEditLead: () => void;
  layoutType?: 'desktop' | 'mobile'
}

export function BookingStripPanel({ state, dispatch, onBook, onJoinWaitlist, onEditLead, layoutType }: BookingStripPanelProps) {
  const { isOpen, toggle, close } = useOpenClose()
  const enablement = getBookingPanelEnablement(state);
  const isMobile = layoutType === 'mobile';
  const hideSelectedPill = isMobile;

  const showLowerPanel = enablement.showPanel && layoutType === 'desktop';

  return (
    <>
      {/* Top strip — always visible. The divider line fades in only when
          the lower panel reveals, so the strip alone (pre-location) doesn't
          have a floating horizontal line below it. */}
      <div
        className={`${styles.stripWrapper} ${showLowerPanel ? styles.stripWrapperWithDivider : ''
          }`}
      >
        <div className={styles.strip}>
          <div className={styles.stepHeader}>
            <img
              src={swingerLogo}
              alt="Club Champion"
              className={styles.stripLogo}
              aria-hidden="true"
            />
            <div className={styles.stepTitle}>
              <h2 className={styles.heading}>Book Your Custom Fitting</h2>
              <p className={`${styles.subtitle} ${styles.learnMoreSubtitle}`} style={{ margin: 0 }}>
                World&apos;s #1 Custom Club Fitter <button className={`${styles.learnMoreButton} ${styles.learnMoreButtonAbsolute}`} onClick={toggle}>Learn More</button>
              </p>
              <SimplePopup
                children={
                  <div>
                    <h3>94% SATISFACTION RATE</h3>
                    <p>Club Champion has an industry leading 94% satisfaction rate across all handicap levels. Golf Digest has named us America’s premier fitter every year over the past decade. Come experience a tour-level, brand agnostic fitting with over 65,000 possible club head and shaft combinations from all the leading manufacturers to see what we can do to improve your game.</p>
                  </div>
                }
                isOpen={isOpen}
                onClosePopup={close}
              />
            </div>
            {isMobile && <MobileSelectionSummary state={state} dispatch={dispatch} onEditLead={onEditLead} />}
          </div>
          {!isMobile && <LeadSummaryRow state={state} onEdit={onEditLead} />}
          {isMobile &&
            <>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="fittingType">
                <FittingTypeField
                  state={state}
                  dispatch={dispatch}
                  disabled={!enablement.fittingTypeEnabled}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="date">
                <DateFieldStrip
                  state={state}
                  dispatch={dispatch}
                  disabled={!enablement.dateAndFitterEnabled}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="fitter">
                <FitterField
                  state={state}
                  dispatch={dispatch}
                  disabled={!enablement.dateAndFitterEnabled}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="time">
                <TimeSlotsPanelWrapper
                  state={state}
                  dispatch={dispatch}
                  onJoinWaitlist={onJoinWaitlist}
                  disabled={!enablement.timeEnabled}
                  hideSelectedPill={state?.selectedTime ? true : false}
                />
              </div>
              <PromoConfirm state={state} dispatch={dispatch} onConfirmFlow={onBook} />
            </>
          }
        </div>
      </div>

      {/* Lower panel — revealed as soon as a location is selected */}
      {showLowerPanel && (
        <div className={styles.panel}>
          <div className={styles.panelGrid}>
            <FittingTypePanel state={state} dispatch={dispatch} />
            <CalendarFitterPanel
              state={state}
              dispatch={dispatch}
              disabled={!enablement.dateAndFitterEnabled}
            />
            <TimeSlotsPanelWrapper
              state={state}
              dispatch={dispatch}
              onJoinWaitlist={onJoinWaitlist}
              disabled={!enablement.timeEnabled}
            />
            <div className={styles.panelFooter}>
              <PromoConfirm state={state} dispatch={dispatch} onConfirmFlow={onBook} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────
   PANEL COLUMNS (revealed after location selection)
   ────────────────────────────────────────────────────────────── */

function LeadSummaryRow({ state, onEdit }: { state: BookingState; onEdit: () => void }) {
  const items: Array<{ label: string; value: string }> = [
    { label: 'Name', value: state.fullName || '—' },
    { label: 'Email', value: state.email || '—' },
    // Phone only shows once collected — the mandatory flavour gathers it on
    // Step 1, the optional flavour not until the last step, so it's absent here.
    ...(state.phone.trim() !== '' ? [{ label: 'Phone', value: state.phone }] : []),
    {
      label: 'Select Studio',
      value: state.selectedLocation
        ? storeDisplayName(state.selectedLocation.name, state.country)
        : '—',
    },
  ];
  return (
    <div className={styles.leadSummaryRow}>
      {items.map((item) => (
        <div key={item.label} className={styles.stripPill}>
          <span className={styles.label}>{item.label}</span>
          <div className={styles.stripPillValue}>
            <span className={styles.leadSummaryValueText}>{item.value}</span>
            <button
              type="button"
              className={styles.stripPillChange}
              onClick={onEdit}
            >
              Change
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function FittingTypePanel({ state, dispatch }: { state: BookingState; dispatch: React.Dispatch<BookingAction> }) {
  const flashMissing = state.showRequiredErrors && !state.selectedFittingType;

  // Keep the selected card in view. A fitting preselected upstream (the seed
  // card's data-cc-fitting) can sit anywhere in the list — Wedge is last —
  // and the panel opened scrolled to the top with the selection invisible.
  // block: 'nearest' makes this a no-op when the card is already visible, so
  // a user's own click never causes a jump.
  const selectedCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    selectedCardRef.current?.scrollIntoView({ block: 'nearest' });
  }, [state.selectedFittingType?.id]);
  const { isOpen: isOpenFittingTypeInfo, close: closeFittingTypeInfo, open: openFittingTypeInfo } = useOpenClose()

  return (
    <div className={flashMissing ? styles.fieldFlash : ''}>
      {flashMissing && (
        <div className={styles.fieldFlashTooltip}>You must select a fitting type.</div>
      )}
      <span className={styles.label}>
        Fitting Type
        <button type='button' onClick={openFittingTypeInfo} className={styles.learnMoreButton}>
          Learn More
        </button>
        <SimplePopup
          children={
            <div>
              <p>Club Champion offers many fitting<br />types at various durations.<br /><br />Scroll to explore and see our<br />most popular options.</p>
            </div>
          }
          isOpen={isOpenFittingTypeInfo}
          onClosePopup={closeFittingTypeInfo} />
      </span>
      {(() => {
        const visible = visibleFittingTypes(state.country, state.selectedLocation);
        return (
          <div className={styles.fittingTypeList}>
            {visible.map((ft) => {
              const selected = state.selectedFittingType?.id === ft.id;
              const disp = fittingDisplayFor(ft, state.country);
              return (
                <div
                  key={ft.id}
                  ref={selected ? selectedCardRef : undefined}
                  className={`${styles.fittingTypeCard} ${selected ? styles.selected : ''}`}
                  onClick={() => dispatch({ type: 'SELECT_FITTING_TYPE', payload: ft })}
                >
                  <div className={styles.fittingTypeHeader}>
                    <span className={styles.fittingTypeName}>{disp.displayName.toUpperCase()}</span>
                    {ft.badge && <span className={styles.fittingTypeBadge}>{ft.badge}</span>}
                  </div>
                  <div className={styles.fittingTypeDescription}>{ft.description}</div>
                  <div className={styles.fittingTypeMeta}>
                    <span><strong>{disp.durationLabel}</strong></span>
                    {(() => {
                      // Prefer the Salesforce-resolved label — the cheaper of
                      // the master and the visitor's own promotion — and fall
                      // back to the authored label only when nothing resolved.
                      const wireName = state.fittingNameMap?.[ft.name] ?? ft.name;
                      const { line1, line2 } =
                        promoPricingLines(
                          state.promoPricing?.[wireName],
                          promoPriceShowsFlatFee(state.country),
                          state.country,
                        ) ??
                        formatPricingLines(
                          pricingLabelFor(ft, state.country),
                          promoPriceShowsFlatFee(state.country),
                        );
                      return (
                        <>
                          <span>
                            {line1.bold && <strong>{line1.bold}</strong>}
                            {line1.rest}
                          </span>
                          <span>
                            {line2.bold && <strong>{line2.bold}</strong>}
                            {line2.rest}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

function CalendarFitterPanel({
  state,
  dispatch,
  disabled,
}: {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled: boolean;
}) {
  const today = new Date();
  const min = today.toISOString().split('T')[0];
  const max = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const { availableDates, loading } = useAvailableDates(
    state.selectedLocation,
    state.selectedFittingType,
    state.selectedFitterId,
    state.fittingNameMap,
  );
  const flashMissingDate = state.showRequiredErrors && !state.selectedDate && !disabled;

  return (
    <div className={flashMissingDate ? styles.fieldFlash : ''}>
      {flashMissingDate && (
        <div className={styles.fieldFlashTooltip}>You must select a date.</div>
      )}
      {/* Desktop: visible calendar grid (always exposed). Mobile: native date input.
          Visibility is toggled via CSS — see widget.module.css [data-cc-layout]
          rules near .calendar / .calendarMobileInput. */}
      <div className={`${styles.calendarDesktop} ${disabled ? styles.panelSectionDisabled : ''}`}>
        <span className={styles.label}>Fitting Date</span>
        {disabled && <span className={styles.disabledFieldMessage}>Select a fitting type to enable this field</span>}
        <Calendar
          selectedDate={state.selectedDate}
          onChange={(d) => dispatch({ type: 'SELECT_DATE', payload: d })}
          locationName={
            state.selectedLocation
              ? storeDisplayName(state.selectedLocation.name, state.country)
              : undefined
          }
          availableDates={availableDates}
          loading={loading}
        />
      </div>
      <div className={styles.calendarMobileInput}>
        <input
          className={styles.input}
          type="date"
          min={min}
          max={max}
          value={state.selectedDate ?? ''}
          disabled={disabled}
          onChange={(e) => dispatch({ type: 'SELECT_DATE', payload: e.target.value || null })}
        />
      </div>
      <FitterField state={state} dispatch={dispatch} disabled={disabled} />
    </div>
  );
}

function TimeSlotsPanelWrapper({
  state,
  dispatch,
  onJoinWaitlist,
  disabled,
  hideSelectedPill = false
}: {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onJoinWaitlist: () => void;
  hideSelectedPill?: boolean;
  disabled: boolean;
}) {

  if (hideSelectedPill) return null

  if (disabled) {
    return (
      <div className={styles.panelSectionDisabled}>
        <span className={styles.label}>Fitting Time</span>
        <span className={styles.disabledFieldMessage}>Select a fitting type and date to see available times</span>
      </div>
    );
  }



  const flashMissingSlot = state.showRequiredErrors && !state.selectedSlot;
  return (
    <div className={flashMissingSlot ? styles.fieldFlash : ''}>
      {flashMissingSlot && (
        <div className={styles.fieldFlashTooltip}>You must select a fitting time.</div>
      )}
      <span className={styles.label}>Fitting Time</span>
      <TimeSlots
        state={state}
        dispatch={dispatch}
        onJoinWaitlist={onJoinWaitlist}
        disabled={disabled}
      />
    </div>
  );
}

